// ABOUTME: Third step in external channel creation - configure sync settings
// ABOUTME: Dynamically renders sync options based on channel type using externalChannelTypeConfigurations

import React, { useEffect, useState } from 'react';
import ProgressionTab from '../../../../theme/ProgressionBuilder/ProgressionTab';
import { useTheme } from '@emotion/react';
import Text from '../../../../theme/text/Text';
import { useTranslation } from 'react-i18next';
import { Box, ToggleButtonGroup, ToggleButton, Alert } from '@mui/material';
import { tokens } from '../../../../../theme';
import { externalChannelTypeConfigurations } from '../../../../../data/externalChannelTypeConfigurations';
import cx from 'classnames';
import ShValidatedInput from '../../../../theme/inputs/ShValidatedInput';
import NumberInput from '../../../../theme/inputs/NumberInput';
import { InputDropdown } from '../../../../theme/dropdowns/InputDropdown';
import EMGeneralExplainerBox from '../../../ModalsPopover/explainers/EMGeneralExplainerBox';
import * as yup from "yup";
import { useSelector } from 'react-redux';
import { yupRequired } from '../../../../../utils/validation';
import ShValidatedInputDropdown from '../../../../theme/dropdowns/ShValidatedInputDropdown';
import { useGetWarehousesSelectQuery } from '../../../../../newapi/warehouse/warehouseApiSlice';
import { useTestExternalChannelConnectionMutation } from '../../../../../newapi/externalchannel/externalChannelSlice';
import Shbutton from '../../../../theme/buttons/Shbutton';
import ShLabel from '../../../../theme/label/ShLabel';

const Content = (props) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const colors = tokens(theme.palette.mode);
    const data = { ...props.data };

    // Get configuration for selected channel type
    const config = externalChannelTypeConfigurations[data.channel_type];

    // A mailbox is tested with all its settings at once, so its test sits on this step rather
    // than next to the api key on the previous one.
    const isMailboxChannel = data.channel_type === 'smtpimap';
    const [testConnection, { isLoading: isTestingConnection }] = useTestExternalChannelConnectionMutation();
    const [connectionStatus, setConnectionStatus] = useState(null); // null, 'success', 'error'
    const [connectionError, setConnectionError] = useState('');

    const handleTestConnection = async () => {
        setConnectionError('');
        try {
            await testConnection({
                id: 'test',
                credentials: { type: data.channel_type, credentials: data.credentials }
            }).unwrap();
            setConnectionStatus('success');
        } catch (error) {
            setConnectionStatus('error');
            setConnectionError(error?.data?.error || error?.data?.message || '');
        }
    };

    const canTestConnection = !!(data.credentials?.emailAddress && data.credentials?.password
        && data.credentials?.smtpHost && data.credentials?.imapHost);

    const connectionTest = isMailboxChannel && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, paddingTop: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2 }}>
                <Shbutton
                    variant="outlined"
                    onClick={handleTestConnection}
                    disabled={!canTestConnection || isTestingConnection}
                    sx={{ minWidth: 150 }}
                >
                    {t('test_connection')}
                </Shbutton>

                {isTestingConnection ? (
                    <ShLabel variant="blue" palette="normal" fitted>{t('connecting')}</ShLabel>
                ) : connectionStatus === 'error' ? (
                    <ShLabel variant="red" palette="normal" fitted>{t('connection_failed')}</ShLabel>
                ) : connectionStatus === 'success' ? (
                    <ShLabel variant="green" palette="normal" fitted>{t('connected')}</ShLabel>
                ) : null}
            </Box>
            {connectionStatus === 'error' && connectionError && !isTestingConnection && (
                <Text light>{connectionError}</Text>
            )}
        </Box>
    );

    // Ampere reports per fulfilment centre, so the channel belongs to one warehouse. The
    // select endpoint already returns only the active, non-bulk warehouses.
    const isWarehouseChannel = data.channel_type === 'ampere';
    const { data: warehouseData, isLoading: isLoadingWarehouses } = useGetWarehousesSelectQuery(undefined, { skip: !isWarehouseChannel });
    const warehouses = warehouseData?.data ?? [];

    // With a single warehouse there is nothing to choose, so link it without asking.
    useEffect(() => {
        if (isWarehouseChannel && warehouses.length === 1 && !data.warehouse_id) {
            props.handleDataUpdate({ warehouse_id: warehouses[0].id });
        }
    }, [isWarehouseChannel, warehouses.length]);

    // Shown unless there is exactly one warehouse to link to; with none it makes the
    // blocking validation visible instead of leaving the save silently stuck.
    const warehouseField = isWarehouseChannel && !isLoadingWarehouses && warehouses.length !== 1 && (
        <Box sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            paddingTop: 3,
        }}>
            <Text>{t('warehouse')}</Text>
            <ShValidatedInputDropdown
                key={data.warehouse_id}
                name="warehouse_id"
                displayName="name"
                changeField="id"
                valueKey="id"
                closeOnSelection
                selected={warehouses.find(item => item.id === data.warehouse_id)?.id ?? -1}
                options={warehouses}
                noSelection={t('choose_option')}
                onChange={(name, value) => props.handleDataUpdate({ [name]: value })}
                error={props.errors?.warehouse_id}
            />
        </Box>
    );

    // Initialize credentials with default values
    useEffect(() => {
        if (config && !data.credentials) {
            props.handleDataUpdate({
                credentials: {
                    ...(data.credentials || {}),
                    ...config.saveObject
                }
            });
        }
    }, [config]);

    const handleBooleanChange = (fieldName, value) => {
        const boolValue = value === "true";
        props.handleDataUpdate({
            credentials: {
                ...data.credentials,
                [fieldName]: boolValue
            }
        });
    };

    const handleInputChange = (name, value) => {
        // A result only holds for the settings it was tested with.
        setConnectionStatus(null);
        props.handleDataUpdate({
            credentials: {
                ...data.credentials,
                [name]: value
            }
        });
    };

    const handleDropdownChange = (baseList, name, value) => {
        const val = baseList[value]?.value;
        if (val) {
            props.handleDataUpdate({
                credentials: {
                    ...data.credentials,
                    [name]: val
                }
            });
        }
    };

    const handleMultiDropdownChange = (baseList, name, values) => {
        const updatedArray = values.map(index => baseList[index].value);
        props.handleDataUpdate({
            credentials: {
                ...data.credentials,
                [name]: updatedArray
            }
        });
    };

    const selectedFieldIndex = (field) => {
        if (Array.isArray(data.credentials?.[field.name])) {
            const selectedIndexes = data.credentials[field.name]
                .map(item => field.options.findIndex(option => option.value === item))
                .filter(index => index !== -1);
            return selectedIndexes;
        }
        return [];
    };

    if (!config) {
        return <Text>Configuration not found for selected channel type</Text>;
    }

    // Determine if any fields are visible here (accounting for conditionals). The api key is
    // the only credential collected on the basic info step; other secrets belong on this one.
    const hasVisibleFields = config.fields.some((field) => {
        if (field.name === 'apiKey') return false;
        if (field.conditional) {
            const fieldValue = field.conditional.value;
            if (Array.isArray(fieldValue)) {
                return fieldValue.includes(data.credentials?.[field.conditional.field]);
            }
            return data.credentials?.[field.conditional.field] === fieldValue;
        }
        return true;
    });

    if (!hasVisibleFields) {
        // Outlook is saved unconnected: the mailbox is linked afterwards from the channel's
        // own settings, so say that here rather than promising it starts by itself.
        const message = data.channel_type === 'outlook'
            ? t('channel_configuration_connect_in_settings')
            : t('channel_configuration_no_options');

        return (
            <>
                {warehouseField}
                <Box sx={{ paddingTop: 3, maxWidth: 560 }}>
                    <Alert severity="info">
                        {message}
                    </Alert>
                </Box>
            </>
        );
    }

    return (
        <>
            {warehouseField}
            {config.fields.map((field) => {
                // Skip the api key, it is handled in the BasicInfo view
                if (field.name === 'apiKey') {
                    return null;
                }

                // Check if field is conditional and should be rendered
                if (field.conditional) {
                    const fieldValue = field.conditional.value;
                    let conditionMet = false;

                    if (Array.isArray(fieldValue)) {
                        conditionMet = fieldValue.includes(data.credentials?.[field.conditional.field]);
                    } else {
                        conditionMet = data.credentials?.[field.conditional.field] === fieldValue;
                    }

                    if (!conditionMet) return null;
                }

                switch (field.type) {
                    case 'text':
                    case 'password':
                        return (
                            <Box key={field.name} sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                paddingTop: 3,
                            }}>
                                <Text>
                                    {t(field.label)}
                                    {field.tooltip && <EMGeneralExplainerBox title={t(field.label)} text={t(field.tooltip)} />}
                                </Text>
                                <ShValidatedInput
                                    name={field.name}
                                    type={field.type}
                                    value={data.credentials?.[field.name] || ''}
                                    onChange={handleInputChange}
                                    error={props.errors?.[`credentials.${field.name}`]}
                                />
                            </Box>
                        );

                    case 'boolean':
                        return (
                            <Box
                                key={field.name}
                                display="flex"
                                flexDirection="row"
                                sx={{
                                    gap: 1,
                                    paddingTop: 3,
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    "@media screen and (max-width:47.9375em)": {
                                        width: 1,
                                        flexDirection: "row",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                    }
                                }}
                            >
                                <Text>
                                    {t(field.label)}
                                    {field.tooltip && <EMGeneralExplainerBox title={t(field.label)} text={t(field.tooltip)} />}
                                </Text>

                                <ToggleButtonGroup
                                    color={data.credentials?.[field.name] ? "green" : "red"}
                                    value={data.credentials?.[field.name] ? "true" : "false"}
                                    exclusive
                                    onChange={(e) => handleBooleanChange(field.name, e.target.value)}
                                    className={cx(`${data.credentials?.[field.name]}`, {
                                        'active': data.credentials?.[field.name]
                                    })}
                                    sx={{
                                        background: colors.red[100],
                                        padding: 0.5,
                                        borderRadius: 5,
                                        transition: "250ms",
                                        "&.active": {
                                            background: colors.green[100],
                                            transition: "250ms",
                                        },
                                    }}
                                >
                                    <ToggleButton
                                        sx={{
                                            padding: 0,
                                            paddingX: 2,
                                            border: 0,
                                            lineHeight: "32px",
                                            textTransform: "unset",
                                            borderRadius: "32px !important",
                                            color: colors.green[400],
                                            transition: "250ms",
                                            "&.Mui-selected,&.Mui-selected:hover": {
                                                background: colors.red[400],
                                                color: `${theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"}`
                                            }
                                        }}
                                        value="false"
                                    >
                                        {t('no')}
                                    </ToggleButton>
                                    <ToggleButton
                                        sx={{
                                            padding: 0,
                                            paddingX: 2,
                                            border: 0,
                                            lineHeight: "32px",
                                            textTransform: "unset",
                                            borderRadius: "32px !important",
                                            color: colors.red[400],
                                            transition: "250ms",
                                            "&.Mui-selected,&.Mui-selected:hover": {
                                                background: colors.green[400],
                                                color: `${theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"}`
                                            }
                                        }}
                                        value="true"
                                    >
                                        {t('yes')}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        );

                    case 'number':
                        return (
                            <Box key={field.name} sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                paddingTop: 3,
                            }}>
                                <Text>
                                    {t(field.label)}
                                    {field.tooltip && <EMGeneralExplainerBox title={t(field.label)} text={t(field.tooltip)} />}
                                </Text>
                                <NumberInput
                                    name={field.name}
                                    value={data.credentials?.[field.name] || field.default || 0}
                                    onChange={(value) => handleInputChange(field.name, value)}
                                    min={field.min}
                                    max={field.max}
                                    step={field.step}
                                />
                            </Box>
                        );

                    case 'dropdown':
                        if (field.translate) {
                            const translatedOptions = field.options.map(option => ({
                                ...option,
                                label: t(option.label)
                            }));
                            field.options = translatedOptions;
                        }
                        return (
                            <Box key={field.name} sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                paddingTop: 3,
                            }}>
                                <Text>
                                    {t(field.label)}
                                    {field.tooltip && <EMGeneralExplainerBox title={t(field.label)} text={t(field.tooltip)} />}
                                </Text>
                                <InputDropdown
                                    key={data.credentials?.[field.name]}
                                    name={field.name}
                                    useTranslation={field.translate}
                                    noSelection={t('choose_option')}
                                    displayName="label"
                                    options={field.options ?? []}
                                    selected={field.options.findIndex(option => option.value === (data.credentials?.[field.name] ?? field.default))}
                                    onChange={(selected) => handleDropdownChange(field.options, field.name, selected)}
                                    closeOnSelection
                                />
                            </Box>
                        );

                    case 'multidropdown':
                        if (field.translate) {
                            const translatedOptions = field.options.map(option => ({
                                ...option,
                                label: t(option.label)
                            }));
                            field.options = translatedOptions;
                        }
                        return (
                            <Box key={field.name} sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                paddingTop: 3,
                            }}>
                                <Text>
                                    {t(field.label)}
                                    {field.tooltip && <EMGeneralExplainerBox title={t(field.label)} text={t(field.tooltip)} />}
                                </Text>
                                <InputDropdown
                                    key={data.credentials?.[field.name]}
                                    name={field.name}
                                    multiple={true}
                                    noSelection={t('choose_option')}
                                    displayName="label"
                                    options={field.options ?? []}
                                    selected={selectedFieldIndex(field)}
                                    onChange={(selected) => handleMultiDropdownChange(field.options, field.name, selected)}
                                    closeOnSelection={false}
                                />
                            </Box>
                        );

                    default:
                        return null;
                }
            })}
            {connectionTest}
        </>
    );
};

const ChannelConfiguration = (props) => {
    const { t } = useTranslation();
    // Ampere cannot authenticate without its own client credentials, so saving a channel
    // that misses one would only produce failing calls.
    const channelType = useSelector((state) => state.progressiontabs.data?.channel_type);
    const schemas = {
        ampere: yup.object({
            warehouse_id: yup.number().required(t('warehouse') + " " + t('validation_required')),
            credentials: yup.object({
                clientId: yupRequired('client_id', t),
                clientSecret: yupRequired('client_secret', t)
            }).required()
        }),
        // The backend logs in on the mailbox before saving; these are what it cannot do without.
        smtpimap: yup.object({
            credentials: yup.object({
                emailAddress: yupRequired('emailaddress', t),
                password: yupRequired('password', t),
                smtpHost: yupRequired('smtp_server', t),
                imapHost: yupRequired('imap_server', t)
            }).required()
        }),
        // The organization id is what PostNL needs on the label; a Zonos channel without it does nothing.
        zonos: yup.object({
            credentials: yup.object({
                organizationId: yupRequired('zonos_organization_id', t)
            }).required()
        })
    };
    const schema = schemas[channelType];

    return (
        <ProgressionTab
            tabId="tabConfiguration"
            subtitle={t("configuration")}
            step={3}
            maxSteps={3}
            schema={schema}
        >
            <Content />
        </ProgressionTab>
    )
}

export default ChannelConfiguration;
